import { Action, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { REHYDRATE } from 'redux-persist';

interface Center {
    lat?: number;
    lng?: number;
}

interface Bounds {
    sw: {
        lat: number;
        lng: number;
    };
    ne: {
        lat: number;
        lng: number;
    };
}

interface MapState {
    mapInitialized: boolean;
    center: Center;
    bounds: Bounds;
    zoom: number;
    isMapMoving: boolean;
    isCenterAvailable: boolean;
    isByMouseMove: boolean;
    isContextualMenuOpen: boolean;
    simulateCenterClick: boolean;
    needsUpdate: boolean;
    searchedLocationMarker?: {
        lat: number;
        lng: number;
        name: string;
    };
}

const initialState: MapState = {
    mapInitialized: false,
    center: {},
    bounds: undefined,
    zoom: -1,
    isMapMoving: false,
    isCenterAvailable: false,
    isByMouseMove: false,
    isContextualMenuOpen: false,
    simulateCenterClick: false,
    needsUpdate: false,
    searchedLocationMarker: undefined,
};

export interface MoveMapToProps {
    center: Center;
    zoom?: number;
}

type RehydrateAction = Action<typeof REHYDRATE> & {
    payload: RehydratePayload | undefined;
};

interface RehydratePayload {
    map?: Partial<MapState>;
}

const mapSlice = createSlice({
    name: 'map',
    initialState,
    reducers: {
        moveMapTo: (state, action: PayloadAction<MoveMapToProps>) => {
            const { center, zoom } = action.payload;
            if (zoom) {
                state.zoom = zoom;
            }
            if (center) {
                state.center = center;
                state.isCenterAvailable = true;
            }
        },
        setMapMoving: (
            state,
            action: PayloadAction<{
                value: boolean;
                center?: {
                    lat: number;
                    lng: number;
                };
            }>
        ) => {
            if (action.payload.center) {
                state.center = action.payload.center;
                state.isByMouseMove = true;
            }
            state.isMapMoving = action.payload.value;
        },
        mouseMapMoveAck: (state) => {
            state.isByMouseMove = false;
        },
        updateCurrentZoom: (state, action: PayloadAction<number>) => {
            state.zoom = action.payload;
        },
        updateCurrentBounds: (state, action: PayloadAction<Bounds>) => {
            state.bounds = action.payload;
        },
        setNeedsUpdate: (state, action: PayloadAction<boolean>) => {
            state.needsUpdate = action.payload;
        },
        setMapInitialized: (state, action: PayloadAction<boolean>) => {
            state.mapInitialized = action.payload;
        },
        toggleContextualMenu: (state, action: PayloadAction<boolean>) => {
            state.isContextualMenuOpen = action.payload;
        },
        triggerCenterClick: (state) => {
            state.simulateCenterClick = !state.simulateCenterClick;
        },
        setSearchedLocationMarker: (
            state,
            action: PayloadAction<{ lat: number; lng: number, name:string } | undefined>
        ) => {
            state.searchedLocationMarker = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(REHYDRATE, (state, action: RehydrateAction) => {
            const rehydratedState = action.payload?.map;
            if (rehydratedState) {
                Object.assign(state, rehydratedState);
                state.isMapMoving = false;
            }
        });
    },
});

export const {
    setMapMoving,
    moveMapTo,
    updateCurrentZoom,
    mouseMapMoveAck,
    setMapInitialized,
    updateCurrentBounds,
    toggleContextualMenu,
    triggerCenterClick,
    setNeedsUpdate,
    setSearchedLocationMarker,
} = mapSlice.actions;

export default mapSlice.reducer;
